import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

/**
 * Shared cache for the published-question count.
 *
 * Several home-page components (Hero badge, TrustBadges stat) used to fire
 * the same `pool_questions count` query independently on mount, doubling the
 * Supabase round-trips on the critical path. This hook fetches once per
 * page load and shares the result across all consumers via a module-level
 * promise + value cache.
 */
const FALLBACK = 3500;

let cached: number | null = null;
let inflight: Promise<number> | null = null;

async function fetchCount(): Promise<number> {
  if (cached !== null) return cached;
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const { count } = await supabase
        .from('pool_questions')
        .select('id', { count: 'exact', head: true })
        .eq('is_published', true);
      const value = typeof count === 'number' && count > 0 ? count : FALLBACK;
      cached = value;
      return value;
    } catch {
      cached = FALLBACK;
      return FALLBACK;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

export function useQuestionCount(initial: number = FALLBACK): number {
  const [count, setCount] = useState<number>(cached ?? initial);
  useEffect(() => {
    if (cached !== null) {
      setCount(cached);
      return;
    }
    let cancelled = false;
    fetchCount().then((v) => {
      if (!cancelled) setCount(v);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return count;
}
